import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Site } from '../../types'
import { Alert, Card, CardBody, CardHeader, Skeleton, StatusBadge } from '../../components/ui'

export default function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'site', siteId],
    queryFn: () => api.get<{ site: Site }>(`/sites/${siteId}`),
    enabled: Boolean(siteId),
  })

  if (!siteId) return <Navigate to="/sites" replace />

  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/sites">Back to sites</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) {
    return <div className="stack"><Skeleton h={30} w="50%" /><Skeleton h={140} /></div>
  }

  const site = data.site

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{site.name}</h1>
          <p className="page-head__sub">
            {[titleCase(site.district_id), titleCase(site.region_id)].filter(Boolean).join(', ') || 'Location not set'}
          </p>
        </div>
        <StatusBadge status={site.validation_status || 'pending'} />
      </div>

      <div className="grid grid--2">
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <div className="stack">
              <Row label="Minerals" value={(site.mineral_types ?? []).map(titleCase).join(', ') || '—'} />
              <Row label="Land tenure" value={titleCase(site.land_tenure_type) || '—'} />
              <Row label="Area" value={site.area_size_ha ? `${formatNumber(site.area_size_ha, 2)} ha` : '—'} />
              <Row label="Village" value={site.village || '—'} />
              <Row label="Registered" value={formatDate(site.created_at)} />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Description" />
          <CardBody>
            <p style={{ margin: 0 }}>{site.description || 'No description provided.'}</p>
            {(site.latitude != null && site.longitude != null) && (
              <p className="page-head__sub" style={{ marginTop: 'var(--sp-3)' }}>
                {site.latitude}, {site.longitude}
              </p>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
